import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, ArrowUpRight, CalendarDays, Check, Clock, Loader2, Sun } from 'lucide-react';
import { api } from '../lib/api';
import { myDayApi, TEAMS, BUCKETS, KIND_COLOUR, isoIn, nextMonday, type Bucket, type MyDay, type MyDayTask, type TaskState } from '../lib/myday';
import { Segmented, PageHeader, Badge, Button, Modal, Field, Input, Select, Textarea, EmptyState } from '../components/ui';
import { AddTask } from '../components/myday/AddTask';

import { StatTile, FilterNote } from '../components/charts/StatTile';
import { useStatFilter } from '../hooks/useStatFilter';
import { FormError } from '../components/forms/common';
import { useAuth } from '../stores/auth';
import { formatDate } from '../lib/viz';

type UserLite = { id: string; email: string; fullName: string | null; roleName: string | null };

const dueText = (t: MyDayTask) => {
  if (!t.due) return null;
  const today = isoIn(0);
  if (t.due < today) return `was due ${formatDate(t.due)}`;
  if (t.due === today) return 'due today';
  if (t.due === isoIn(1)) return 'due tomorrow';
  return `due ${formatDate(t.due)}`;
};

/**
 * Everything waiting on you, pulled out of the platform and put in one place (prototype
 * §36). Nothing here is stored but your decisions: tick it off and it goes — it comes
 * back if the record it is about changes and still needs doing.
 */
export default function MyDayPage() {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const [show, setShow] = useState<TaskState>('open');
  // The four tiles are filters on the open list (prototype §38): ?filter=overdue | today | week | later.
  const tf = useStatFilter<Bucket>();
  const [who, setWho] = useState<string>('me');
  const [whoLabel, setWhoLabel] = useState<string>('');
  const [team, setTeam] = useState('');
  const [handing, setHanding] = useState<MyDayTask | null>(null);
  const [adding, setAdding] = useState(false);
  const canWrite = can('myDay.update');
  const canPeople = can('users.read');

  const q = useQuery({
    queryKey: ['/my-day', { who, team, show }],
    queryFn: () => myDayApi.list({ who: who === 'me' ? undefined : who, team: team || undefined, show }),
    refetchInterval: 60_000,
  });
  const day = q.data;

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['/my-day'] }); };
  const act = useMutation({
    mutationFn: async (fn: () => Promise<unknown>) => fn(),
    onSuccess: refresh,
  });

  const counts = day?.counts;

  return (
    <div>
      <PageHeader title="My Day"
        menu={[canWrite && { label: 'Add a task', icon: 'Plus', onClick: () => setAdding(true) }, { label: 'Export CSV', export: { path: '/my-day', page: 'myDay', query: { who: who === 'me' ? undefined : who, team: team || undefined, show }, filename: 'my-day.csv' } }]} />
      {adding && <AddTask onClose={() => setAdding(false)} onDone={() => { setAdding(false); refresh(); }} />}

      <div className="mb-2.5 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <StatTile label="Overdue" {...(show === 'open' ? tf.tile('overdue') : {})} value={counts ? String(counts.overdue) : '—'} status={counts?.overdue ? 'critical' : undefined} hint="Past their date" icon={<AlarmClock className="h-4 w-4" />} />
        <StatTile label="Today" {...(show === 'open' ? tf.tile('today') : {})} value={counts ? String(counts.today) : '—'} hint="Due today" icon={<Sun className="h-4 w-4" />} />
        <StatTile label="This week" {...(show === 'open' ? tf.tile('week') : {})} value={counts ? String(counts.week) : '—'} hint="Due in the next 7 days" icon={<CalendarDays className="h-4 w-4" />} />
        <StatTile label="Later" {...(show === 'open' ? tf.tile('later') : {})} value={counts ? String(counts.later) : '—'} hint={counts ? `${counts.snoozed} snoozed · ${counts.done} done` : undefined} icon={<Clock className="h-4 w-4" />} />
      </div>

      <div className="surface mb-2.5 flex flex-wrap items-center gap-2 px-3 py-2">
        <Segmented value={show} onChange={setShow}
          items={(['open', 'snoozed', 'done'] as const).map((s) => ({ key: s, label: s === 'open' ? 'Open' : s === 'snoozed' ? 'Snoozed' : 'Done', count: counts ? counts[s] : null }))} />
        <div className="hidden flex-1 sm:block" />
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Select value={who === 'me' || who === 'all' ? who : 'person'} onChange={(e) => { if (e.target.value !== 'person') { setWho(e.target.value); setWhoLabel(''); } }} className="min-w-0 flex-1 sm:flex-none" aria-label="Whose list">
            <option value="me">Mine</option>
            <option value="all">Everyone</option>
            {who !== 'me' && who !== 'all' && <option value="person">{whoLabel || 'A person'}</option>}
          </Select>
          {canPeople && <PersonPicker placeholder="Someone's day…" onPick={(u) => { setWho(u.id === user?.id ? 'me' : u.id); setWhoLabel(u.fullName ?? u.email); }} className="order-last w-full sm:order-none sm:w-48" />}
          <Select value={team} onChange={(e) => setTeam(e.target.value)} className="min-w-0 flex-1 sm:flex-none" aria-label="Team">
            <option value="">Every team</option>
            {TEAMS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </Select>
          {q.isFetching && <Loader2 className="h-4 w-4 animate-spin text-bronze-400" />}
        </div>
      </div>

      {q.isLoading ? (
        <div className="surface space-y-3 p-4">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 animate-pulse rounded-xl bg-ink-100" />)}</div>
      ) : q.error ? (
        <EmptyState icon="TriangleAlert" title="Could not load your day" body={(q.error as Error).message} />
      ) : !day || day.tasks.length === 0 ? (
        <EmptyState icon="CircleCheck" title={show === 'open' ? 'Nothing waiting' : show === 'snoozed' ? 'Nothing snoozed' : 'Nothing done yet'}
          body={show === 'open' ? 'Nothing late, due or unanswered. Rare and lovely.' : undefined} />
      ) : show === 'open' ? (
        <div className="space-y-2.5">
          {tf.active && <FilterNote label={BUCKETS.find((b) => b.key === tf.active)?.label.toLowerCase()} count={day.tasks.filter((t) => t.bucket === tf.active).length} onClear={tf.clear} />}
          {BUCKETS.filter((b) => !tf.active || b.key === tf.active).map((b) => {
            const rows = day.tasks.filter((t) => t.bucket === b.key);
            if (!rows.length) return null;
            return (
              <section key={b.key}>
                <div className="mb-2 flex items-center gap-3 px-1">
                  <h2 className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${b.key === 'overdue' ? 'text-red-600' : 'text-ink-500'}`}>{b.label}</h2>
                  <span className="h-px flex-1 bg-ink-200/80" />
                  <span className="text-[11.5px] tabular-nums text-ink-400">{rows.length}</span>
                </div>
                <TaskList rows={rows} day={day} canWrite={canWrite} busy={act.isPending} act={(fn) => act.mutate(fn)} onHand={setHanding} />
              </section>
            );
          })}
        </div>
      ) : (
        <TaskList rows={day.tasks} day={day} canWrite={canWrite} busy={act.isPending} act={(fn) => act.mutate(fn)} onHand={setHanding} />
      )}
      {act.error && <div className="mt-3"><FormError error={act.error} /></div>}

      {handing && <HandOver task={handing} onClose={() => setHanding(null)} onDone={() => { setHanding(null); refresh(); }} />}
    </div>
  );
}

function TaskList({ rows, day, canWrite, busy, act, onHand }: { rows: MyDayTask[]; day: MyDay; canWrite: boolean; busy: boolean; act: (fn: () => Promise<unknown>) => void; onHand: (t: MyDayTask) => void }) {
  return (
    <div className="surface divide-y divide-ink-100 overflow-hidden">
      {rows.map((t) => <TaskRow key={t.key} t={t} showOwner={day.who.all || !!t.handed} canWrite={canWrite} busy={busy} act={act} onHand={onHand} />)}
    </div>
  );
}

function TaskRow({ t, showOwner, canWrite, busy, act, onHand }: { t: MyDayTask; showOwner: boolean; canWrite: boolean; busy: boolean; act: (fn: () => Promise<unknown>) => void; onHand: (t: MyDayTask) => void }) {
  const late = t.state === 'open' && t.overdue;
  const colour = late ? '#d9534f' : KIND_COLOUR[t.kind] ?? '#8fa3b8';
  const due = dueText(t);
  return (
    <div className="relative flex gap-3 px-4 py-3.5 pl-5" style={{ boxShadow: `inset 3px 0 0 ${colour}` }}>
      {canWrite && t.state !== 'snoozed' ? (
        <button
          disabled={busy}
          onClick={() => act(() => (t.state === 'done' ? myDayApi.reopen(t.key) : myDayApi.done(t.key)))}
          title={t.state === 'done' ? 'Put it back' : 'Tick it off'}
          className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border transition ${t.state === 'done' ? 'border-emerald-600 bg-emerald-600 text-on-primary' : 'border-ink-300 text-transparent hover:border-emerald-600 hover:text-emerald-600'}`}>
          <Check className="h-3 w-3" strokeWidth={3.4} />
        </button>
      ) : <span className="mt-0.5 h-5 w-5 shrink-0" />}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5">
          <div className="min-w-0 flex-1">
            <div className={`text-[13.5px] font-medium ${t.state === 'done' ? 'text-ink-400 line-through' : 'text-ink-900'}`}>{t.title}</div>
            <div className="mt-0.5 text-[12px] leading-relaxed text-ink-500">{t.detail}</div>
            {t.handed && (
              <div className="mt-1 text-[11.5px] text-bronze-600">
                Handed {t.ownerName ? `to ${t.ownerName} ` : ''}by {t.handed.byName ?? 'a colleague'}{t.handed.note ? ` — “${t.handed.note}”` : ''}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
            {due && <span className={late ? 'font-medium text-red-600' : 'text-ink-500'}>{due}</span>}
            {t.severity === 'high' && t.state === 'open' && <Badge value="Urgent" tone="critical" />}
            <Badge value={t.kindLabel} tone="neutral" />
            {showOwner && t.ownerName && <span className="rounded-full bg-ink-100 px-2 py-[3px] text-ink-600">{t.ownerName}</span>}
            {!t.ownerName && <span className="text-ink-400">{t.teamLabel}</span>}
            {t.state === 'snoozed' && t.snoozedUntil && <span className="text-amber-700">back {formatDate(t.snoozedUntil)}</span>}
            {t.state === 'done' && t.decidedByName && <span className="text-ink-400">ticked by {t.decidedByName}</span>}
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {canWrite && t.state === 'open' && <SnoozeMenu disabled={busy} onSnooze={(until) => act(() => myDayApi.snooze(t.key, until))} />}
          {canWrite && t.state === 'open' && <Button size="sm" variant="ghost" icon="UserRoundPlus" onClick={() => onHand(t)} disabled={busy} className="!px-2 !py-1">Hand over</Button>}
          {canWrite && t.state === 'snoozed' && <Button size="sm" variant="ghost" icon="RotateCcw" onClick={() => act(() => myDayApi.reopen(t.key))} disabled={busy} className="!px-2 !py-1">Un-snooze</Button>}
          {t.url && (
            <Link to={t.url} className="inline-flex items-center gap-1 rounded-xl px-2 py-1 text-[12px] font-medium text-bronze-600 transition hover:bg-bronze-50">
              Open {t.record.label}<ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/** Tomorrow · next Monday · pick a date. */
function SnoozeMenu({ onSnooze, disabled }: { onSnooze: (until: string) => void; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);
  const [date, setDate] = useState(isoIn(2));
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setPicking(false); } };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const pick = (d: string) => { setOpen(false); setPicking(false); onSnooze(d); };
  const item = 'block w-full rounded-lg px-3 py-2 text-left text-[12.5px] text-ink-700 hover:bg-ink-50';
  return (
    <div className="relative" ref={ref}>
      <Button size="sm" variant="ghost" icon="AlarmClockOff" onClick={() => setOpen((v) => !v)} disabled={disabled} className="!px-2 !py-1">Snooze</Button>
      {open && (
        <div className="absolute left-0 top-full z-20 mt-1 w-56 rounded-xl border border-ink-200 bg-white p-1.5 shadow-pop">
          <button className={item} onClick={() => pick(isoIn(1))}>Tomorrow <span className="float-right text-ink-400">{formatDate(isoIn(1)).slice(0, 6)}</span></button>
          <button className={item} onClick={() => pick(nextMonday())}>Next Monday <span className="float-right text-ink-400">{formatDate(nextMonday()).slice(0, 6)}</span></button>
          {!picking ? (
            <button className={item} onClick={() => setPicking(true)}>Pick a date…</button>
          ) : (
            <div className="flex items-center gap-1.5 p-1.5">
              <Input type="date" value={date} min={isoIn(1)} onChange={(e) => setDate(e.target.value)} className="min-w-0 flex-1 !px-2 !py-1.5" />
              <Button size="sm" onClick={() => date > isoIn(0) && pick(date)} disabled={!date || date <= isoIn(0)}>Go</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Search staff by name or email (GET /users needs users.read). */
function PersonPicker({ onPick, placeholder, className = '' }: { onPick: (u: UserLite) => void; placeholder?: string; className?: string }) {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const users = useQuery({
    queryKey: ['/users', 'picker', term],
    queryFn: async () => (await api.get<UserLite[]>('/users', { search: term || undefined, isActive: true, perPage: 8, sort: 'first_name' })).data,
    enabled: open,
  });
  return (
    <div className={`relative ${className}`}>
      <Input value={term} placeholder={placeholder} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} onChange={(e) => { setTerm(e.target.value); setOpen(true); }} className="w-full" />
      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-xl border border-ink-200 bg-white p-1.5 shadow-pop">
          {users.isLoading && <div className="px-3 py-2 text-[12px] text-ink-400">Searching…</div>}
          {users.data?.length === 0 && <div className="px-3 py-2 text-[12px] text-ink-400">Nobody matches.</div>}
          {users.data?.map((u) => (
            <button key={u.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(u); setTerm(''); setOpen(false); }} className="block w-full rounded-lg px-3 py-2 text-left hover:bg-ink-50">
              <div className="text-[12.5px] font-medium text-ink-800">{u.fullName ?? u.email}</div>
              <div className="text-[11px] text-ink-400">{u.email}{u.roleName ? ` · ${u.roleName}` : ''}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HandOver({ task, onClose, onDone }: { task: MyDayTask; onClose: () => void; onDone: () => void }) {
  const { can } = useAuth();
  const canPick = can('users.read');
  const [to, setTo] = useState<UserLite | null>(null);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const send = useMutation({
    mutationFn: () => myDayApi.handOver(task.key, to ? { userId: to.id } : { email: email.trim() }, note.trim()),
    onSuccess: onDone,
  });
  const ready = to ? true : /.+@.+\..+/.test(email.trim());
  return (
    <Modal open onClose={onClose} title="Hand it over"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button icon="UserRoundPlus" onClick={() => send.mutate()} disabled={!ready || send.isPending}>{send.isPending ? 'Handing over…' : 'Hand over'}</Button></>}>
      <div className="space-y-2.5">
        <div className="rounded-xl bg-ink-50 px-4 py-3">
          <div className="text-[13px] font-medium text-ink-900">{task.title}</div>
          <div className="mt-0.5 text-[12px] text-ink-500">{task.detail}</div>
        </div>
        {canPick ? (
          <Field label="To" hint="It moves to their list and they get a notification.">
            {to ? (
              <div className="flex items-center justify-between rounded-xl border border-ink-200 px-3.5 py-2.5 text-[13px]">
                <span><span className="font-medium text-ink-900">{to.fullName ?? to.email}</span> <span className="text-ink-400">{to.email}</span></span>
                <button onClick={() => setTo(null)} className="text-[12px] text-bronze-600 hover:underline">Change</button>
              </div>
            ) : <PersonPicker placeholder="Search by name or email…" onPick={setTo} />}
          </Field>
        ) : (
          <Field label="Their email" hint="It moves to their list and they get a notification.">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@lotusliving.co.uk" className="w-full" />
          </Field>
        )}
        <Field label="Note (optional)"><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="w-full" placeholder="Anything they need to know" /></Field>
        <FormError error={send.error} />
      </div>
    </Modal>
  );
}

